/**
 * Professional Positions List Component
 * Clean portfolio display with strategic P&L color usage
 */

import { useEffect, memo } from 'react'
import { useTradingStore } from '@/stores/useTradingStore'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Skeleton } from './ui/loading-spinner'
import { formatCurrency, formatPercent } from '@/lib/utils'
import { 
  X, 
  BarChart3, 
  PieChart
} from 'lucide-react'
import type { Position } from '@/types'

interface PositionsListProps {
  onClosePosition?: (position: Position) => void
}

export const PositionsList = memo(function PositionsList({ onClosePosition }: PositionsListProps) {
  const { positions, isLoading, loadPositions } = useTradingStore()

  useEffect(() => {
    // Initial load only - no polling
    loadPositions()
  }, [loadPositions])

  if (isLoading && positions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Open Positions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (positions.length === 0) {
    return (
      <Card className="bg-card/50 backdrop-blur-sm border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
              <PieChart className="h-5 w-5 text-primary-foreground" />
            </div>
            Portfolio Positions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-foreground font-medium mb-2">No Open Positions</div>
            <div className="text-sm text-muted-foreground">
              Start trading to build your portfolio
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const totalPnL = positions.reduce((sum, pos) => sum + (pos.unrealizedPnL || 0), 0)
  const totalPnLPercent = positions.length > 0 
    ? positions.reduce((sum, pos) => sum + (pos.unrealizedPnLPercent || 0), 0) / positions.length 
    : 0

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="w-7 h-7 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center">
              <PieChart className="h-4 w-4 text-primary-foreground" />
            </div>
            Open Positions
            <Badge variant="outline" className="ml-1 border-border text-xs">
              {positions.length}
            </Badge>
          </CardTitle>
          
          {/* Total P&L Summary - Compact */}
          <div className="text-right">
            <div className={`text-lg font-bold ${
              totalPnL >= 0 ? 'text-profit' : 'text-loss'
            }`}>
              {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
            </div>
            <div className={`text-xs ${totalPnL >= 0 ? 'text-profit' : 'text-loss'}`}>
              {formatPercent(totalPnLPercent, 2)}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3">
          {positions.map((position) => {
            const isProfit = (position.unrealizedPnL || 0) >= 0
            const pnl = position.unrealizedPnL || 0
            const pnlPercent = position.unrealizedPnLPercent || 0

            return (
              <div
                key={position.id}
                className="relative p-3 rounded-lg bg-card border border-border hover:shadow-md transition-all duration-200"
              >
                {/* Compact Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge 
                      variant={position.direction === 'BUY' ? 'default' : 'outline'}
                      className={`text-xs px-1.5 py-0.5 ${
                        position.direction === 'BUY' 
                          ? 'bg-primary text-primary-foreground border-0' 
                          : 'border-border'
                      }`}
                    >
                      {position.direction}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-foreground truncate">{position.instrument}</div>
                      <div className="text-xs text-muted-foreground truncate">{position.instrumentName || position.instrument}</div>
                    </div>
                  </div>
                  
                  {/* P&L - Prominent */}
                  <div className="text-right mr-2">
                    <div className={`text-base font-bold ${
                      isProfit ? 'text-profit' : 'text-loss'
                    }`}>
                      {isProfit ? '+' : ''}{formatCurrency(pnl)}
                    </div>
                    <div className={`text-xs ${isProfit ? 'text-profit' : 'text-loss'}`}>
                      {formatPercent(pnlPercent, 2)}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onClosePosition?.(position)}
                    className="h-7 w-7 hover:bg-destructive/10 text-destructive hover:text-destructive flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Compact Details */}
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground mb-0.5">Size</div>
                    <div className="font-medium text-foreground">{position.size || 0}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-0.5">Leverage</div>
                    <div className="font-medium text-foreground">{position.leverage || 1}x</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-0.5">Entry</div>
                    <div className="font-medium text-foreground">{formatCurrency(position.entryPrice || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-0.5">Current</div>
                    <div className="font-medium text-foreground">{formatCurrency(position.currentPrice || 0)}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
})
